import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useDebounced } from '../lib/useDebounced';
import { useVehicleFleet } from '../lib/queries';
import { Card, EmptyState, Pill, SectionTitle, Skeleton } from '../components/ui';

export function VehiclesPage() {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const debounced = useDebounced(search);
  const { data, isPending } = useVehicleFleet(debounced, includeArchived);

  const vehicles = data?.items ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="mb-1 font-display text-[2rem] font-bold tracking-tight text-ink">{t('vehicles.title')}</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('vehicles.searchPlaceholder')}
          aria-label={t('vehicles.search')}
          className="min-w-0 flex-1 rounded-xl border border-line bg-raised px-3.5 py-2.5 text-ink placeholder:text-ink-faint focus:border-brand focus:outline-none"
        />
        <label className="flex min-h-11 shrink-0 cursor-pointer items-center gap-2 text-sm text-ink-dim">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
            className="size-5 accent-[var(--color-brand)]"
          />
          {t('vehicles.includeArchived')}
        </label>
      </div>

      {isPending && <Card><Skeleton className="h-24" /></Card>}

      {!isPending && vehicles.length === 0 && (
        <EmptyState title={debounced ? t('vehicles.emptySearch') : t('vehicles.empty')} />
      )}

      {vehicles.length > 0 && (
        <Card>
          <SectionTitle>{t('vehicles.count', { count: vehicles.length })}</SectionTitle>
          <div className="space-y-1.5">
            {vehicles.map((vehicle) => (
              <Link
                key={vehicle.id}
                to={`/vehicles/${vehicle.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-line bg-raised px-3.5 py-2.5 transition-colors hover:border-brand/50"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-ink">
                      {vehicle.make} {vehicle.model}
                    </span>
                    {vehicle.year && <span className="text-xs text-ink-faint">{vehicle.year}</span>}
                    {vehicle.archivedAt && <Pill>{t('vehicles.archived')}</Pill>}
                  </div>
                  <div className="tabular mt-0.5 truncate text-[0.68rem] text-ink-faint">
                    {vehicle.licensePlate ?? t('vehicles.noPlate')} · {vehicle.ownerName}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="tabular text-xs text-ink-dim">
                    {t('vehicles.jobs', { count: vehicle.orders })}
                  </div>
                  <div className="text-[0.68rem] text-ink-faint">
                    {vehicle.lastServicedAt ? formatDate(vehicle.lastServicedAt) : t('vehicles.neverIn')}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
